import type { ComponentChildren } from 'preact';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'preact/hooks';
import {
  MOON_BID,
  SUITS,
  SUIT_NAMES,
  cardName,
  isPlayable,
  strength,
  type Action,
  type Card as CardCode,
  type GameView,
  type Suit,
} from '../engine';
import { isMuted, setMuted } from './audio';
import { Card, CardBack, SUIT_GLYPH } from './cards';
import { myMove, useGameSounds } from './effects';
import { RulesSheet } from './rules';
import { useKeepAwakeSetting, useWakeLock, wakeLockSupported } from './wake';
import { calledNotice, calledPending, roleTag, sideLine, sideOf, sidesSummary } from './sides';
import { sounds } from './audio';
import type { GameClient } from './game-client';

const SUIT_SORT: Record<string, number> = { S: 0, H: 1, C: 2, D: 3 };
const RANK_SORT = 'AKQJT98765432';

function sortHand(cards: readonly CardCode[], trump: Suit | null): CardCode[] {
  return cards.slice().sort((a, b) => {
    if (trump) {
      const d = strength(b, trump) - strength(a, trump);
      if (d !== 0) return d;
    }
    const ja = a === 'BJ' || a === 'LJ';
    const jb = b === 'BJ' || b === 'LJ';
    if (ja || jb) return ja && jb ? (a === 'BJ' ? -1 : 1) : ja ? -1 : 1;
    return SUIT_SORT[a[1]!]! - SUIT_SORT[b[1]!]! || RANK_SORT.indexOf(a[0]!) - RANK_SORT.indexOf(b[0]!);
  });
}

const FAST = typeof location !== 'undefined' && new URLSearchParams(location.search).has('fast');
/** How long the hand result stays up (tests with ?fast=1 skip through). */
const RESULT_MS = FAST ? 300 : 5000;
/** The shuffle and deal animation. */
const DEAL_MS = FAST ? 100 : 1500;

/** "A♥", "10♣", or "High Joker": compact enough for the felt. */
function shortCard(card: CardCode): string {
  if (card === 'BJ' || card === 'LJ') return cardName(card);
  return `${card[0] === 'T' ? '10' : card[0]}${SUIT_GLYPH[card[1] as Suit]}`;
}

/** "7 ♣", or "the moon ♣". */
export function bidLabel(amount: number, suit: Suit | null): string {
  const glyph = suit ? ` ${SUIT_GLYPH[suit]}` : '';
  return amount === MOON_BID ? `the moon${glyph}` : `${amount}${glyph}`;
}

function waiting(view: GameView): number[] {
  const p = view.phase;
  if (p.kind === 'bid' || p.kind === 'play') return [p.turn];
  if (p.kind === 'call' || p.kind === 'pass') return [view.hand.bidder!];
  if (p.kind === 'trim') return p.pending.flatMap((x, i) => (x ? [i] : []));
  return [];
}

/** The last trick's winner, if they'd normally lead now but have no cards left. */
function outWinner(view: GameView): number | null {
  const h = view.hand;
  if (view.phase.kind !== 'play' || !h.lastTrick || (h.trick && h.trick.plays.length > 0)) return null;
  const w = h.lastTrick.winner;
  return w !== view.phase.turn && (h.handCounts[w] ?? 0) === 0 ? w : null;
}

function statusText(view: GameView, names: readonly string[], seat: number | null): string {
  const p = view.phase;
  const who = (i: number) => (i === seat ? 'You' : names[i] ?? '?');
  const h = view.hand;
  switch (p.kind) {
    case 'bid':
      return p.turn === seat ? 'Your bid' : `${who(p.turn)} to bid`;
    case 'call':
      return h.bidder === seat ? "Call your partner's card" : `${who(h.bidder!)} is calling a partner`;
    case 'pass':
      return h.bidder === seat ? 'Pass kitty cards' : `${who(h.bidder!)} is passing cards`;
    case 'trim':
      return seat !== null && p.pending[seat] ? 'Choose the cards you keep' : 'Everyone is choosing their cards';
    case 'play': {
      // The trick's winner leads next, unless that was their last card: then the next player with cards does.
      const out = outWinner(view);
      if (out !== null) {
        return p.turn === seat ? `${who(out)} is out of cards, so you lead` : `${who(out)} is out of cards, so ${who(p.turn)} leads`;
      }
      return p.turn === seat ? 'Your play' : `${who(p.turn)} to play`;
    }
    case 'gameOver':
      return p.winners.length === 1 ? `${who(p.winners[0]!)} win${p.winners[0] === seat ? '' : 's'}!` : 'A tie!';
  }
}

export function Table({
  client,
  onExit,
  onRematch,
  extraControls,
}: {
  client: GameClient;
  onExit?: () => void;
  /** Same seats and rules, a new deal (hot-seat). */
  onRematch?: () => void;
  /** More footer switches from the wrapper (hot-seat's computer speed). */
  extraControls?: ComponentChildren;
}) {
  const { view, seat, legal, names } = client;
  const h = view.hand;
  const phase = view.phase;
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<CardCode[]>([]);
  const [bidSuit, setBidSuit] = useState<Suit | null>(null);
  const [bidAmount, setBidAmount] = useState<number | null>(null);
  const [showRules, setShowRules] = useState(false);
  const [muted, setMutedState] = useState(isMuted);
  useGameSounds(client);
  const [keepAwake, setKeepAwake] = useKeepAwakeSetting();
  useWakeLock(keepAwake && phase.kind !== 'gameOver');

  const mine = seat !== null ? (h.hands[seat] ?? []) : [];
  const trump = h.trump;
  const myTrim = phase.kind === 'trim' && seat !== null && phase.pending[seat];
  const playable = trump ? mine.filter((c) => isPlayable(c, trump)) : [];
  const keepCount = Math.min(view.config.rules.handLimit, playable.length);
  const required = h.called && playable.includes(h.called) ? [h.called] : [];
  const myPass = phase.kind === 'pass' && seat === h.bidder;
  // While passing, the kitty cards stay in their own row until they're passed or kept.
  const kittyRow = myPass ? (h.kittyCards ?? []).filter((c) => mine.includes(c)) : [];
  const givable = myPass && trump ? kittyRow.filter((c) => isPlayable(c, trump) && c !== h.called) : [];

  // Fresh selection each step; in the trim, start from "everything you can keep" when it fits.
  // The hand is a dependency too: a reconnect or late view can change it within a step, and a
  // selection naming cards you no longer hold would leave Keep stuck.
  useEffect(() => {
    setSelected(myTrim && playable.length <= keepCount ? playable : myTrim ? required : []);
    setBidSuit(null);
    setBidAmount(null);
  }, [phase.kind, h.number, seat, myTrim, mine.join(',')]);

  // A raise elsewhere can make the number you'd picked too low; drop it rather than send a bad bid.
  const bidAmounts = legal.flatMap((a) => (a.type === 'bid' && a.amount > 0 ? [a.amount] : []));
  const biddingNow = legal.some((a) => a.type === 'bid');
  useEffect(() => {
    if (bidAmount !== null && !bidAmounts.includes(bidAmount)) setBidAmount(null);
  }, [bidAmounts.join(',')]);

  // The hand result card: shown when a hand is scored, until "Next hand", ~5 s, or your next move.
  const [resultSeen, setResultSeen] = useState(view.history.length);
  const showingResult = view.history.length > resultSeen;
  const [dealing, setDealing] = useState(view.history.length === 0 && phase.kind === 'bid' && h.bids.every((b) => b === null));
  const dismissResult = () => {
    if (!showingResult) return;
    setResultSeen(view.history.length);
    if (view.phase.kind !== 'gameOver') setDealing(true);
  };
  useEffect(() => {
    if (!showingResult) return;
    const t = setTimeout(dismissResult, RESULT_MS);
    return () => clearTimeout(t);
  }, [showingResult, view.history.length]);
  useEffect(() => {
    client.holdBots?.(showingResult || dealing);
  }, [showingResult, dealing]);
  useEffect(() => {
    if (!dealing) return;
    sounds.shuffle();
    const t = setTimeout(() => setDealing(false), DEAL_MS);
    return () => clearTimeout(t);
  }, [dealing]);

  useEffect(() => {
    if (client.notice) setError(client.notice.text);
  }, [client.notice?.id]);

  const run = (action: Action) => {
    dismissResult();
    setDealing(false);
    const err = client.act(action);
    setError(err);
  };

  const playableNow = new Set(legal.flatMap((a) => (a.type === 'play' ? [a.card] : [])));
  const selecting = myTrim || givable.length > 0;
  const selectable = (c: CardCode) => (myTrim ? playable.includes(c) && !required.includes(c) : givable.includes(c));
  const toggle = (c: CardCode) =>
    setSelected((sel) => (sel.includes(c) ? sel.filter((x) => x !== c) : [...sel, c]));

  const inHand = kittyRow.length > 0 ? mine.filter((c) => !kittyRow.includes(c)) : mine;
  const sortedHand = useMemo(() => sortHand(inHand, trump), [inHand.join(','), trump]);
  const waitingOn = waiting(view);
  const yourTurn = myMove(client);
  const latest = client.log[client.log.length - 1];
  // The holder of the called card must lead it: everything else greys out.
  const mustPlayCalled = !!h.called && playableNow.size === 1 && playableNow.has(h.called);
  const calledLive = calledPending(view);
  const cardProps = (c: CardCode) => {
    const canPlay = playableNow.has(c);
    const canSelect = selecting && selectable(c);
    const locked = myTrim && required.includes(c);
    // While bidding, a card picks its suit (not Jacks, 3s or jokers, which count in two suits or none).
    const suitPick = biddingNow && c !== 'BJ' && c !== 'LJ' && c[0] !== 'J' && c[0] !== '3';
    const previewSuit = trump ?? (biddingNow ? bidSuit : null);
    return {
      selected: selected.includes(c) || locked,
      dim: (!!previewSuit && !isPlayable(c, previewSuit)) || (mustPlayCalled && c !== h.called),
      extraClass: calledLive && c === h.called ? 'called' : '',
      ...(calledLive && c === h.called ? { tag: 'CALLED' } : {}),
      ...(canPlay
        ? { onClick: () => run({ type: 'play', card: c }) }
        : canSelect
          ? { onClick: () => toggle(c) }
          : suitPick
            ? { onClick: () => setBidSuit(c[1] as Suit) }
            : {}),
    };
  };
  // When there was nothing to pass, the bidder still sees what the kitty held, and why, until the first trick ends.
  const kittyRecap = (() => {
    if (seat === null || seat !== h.bidder || !h.kittyCards || !trump || myPass || h.lastTrick) return null;
    if (phase.kind !== 'trim' && phase.kind !== 'play') return null;
    const playableKitty = h.kittyCards.filter((c) => isPlayable(c, trump));
    if (playableKitty.length === 0) return 'No playable cards in the kitty.';
    if (h.partner === null) return "You're playing alone, so there's nobody to pass to.";
    if (h.passedCount === null) return 'Nothing in the kitty could be passed.';
    return null;
  })();
  const notice = calledNotice(view, seat, names);
  const side = sideLine(view, seat, names);
  const summary = sidesSummary(view, seat, names);
  const lastResult = view.history[view.history.length - 1];

  return (
    <div class="table">
      <TableRing client={client} waitingOn={waitingOn} dealing={dealing}>
        <div class={`status ${yourTurn ? 'yours' : ''}`} data-testid="status">
          {statusText(view, names, seat)}
        </div>
        {trump && (
          <div class="contract" data-testid="contract">
            <div title={h.called ? `Called the ${cardName(h.called)}` : undefined}>
              {names[h.bidder!]} bid {bidLabel(h.bid!, null)} <span class={`suit ${trump}`}>{SUIT_GLYPH[trump]}</span>
              {h.called && <> · called {shortCard(h.called)}</>}
            </div>
            {summary && seat === null && (
              <div class="sides-tag" data-testid="partner">
                {summary}
              </div>
            )}
          </div>
        )}
        {showingResult && lastResult ? (
          <div class={`result-headline ${lastResult.made ? 'made' : 'set'}`} data-testid="result-headline">
            {resultHeadline(lastResult, names, seat)}
          </div>
        ) : calledLive && h.called && !(h.trick && h.trick.plays.length > 0) ? (
          <div class="called-show" data-testid="called-show">
            <Card card={h.called} width={56} />
            <span class="small">
              {names[h.bidder!]} called the {cardName(h.called)}
            </span>
          </div>
        ) : (
          <Trick view={view} names={names} seat={seat} />
        )}
        {latest && (
          <div class="ticker" data-testid="ticker" key={latest.id} aria-live="polite">
            {latest.text}
          </div>
        )}
      </TableRing>

      {error && (
        <div class="toast error" role="alert" onClick={() => setError(null)}>
          {error}
        </div>
      )}

      {phase.kind === 'gameOver' && (
        <div class="panel winner" data-testid="winner">
          <h2>{statusText(view, names, seat)}</h2>
          <div class="row wrap">
            {onRematch && (
              <button class="primary" data-testid="rematch" onClick={onRematch}>
                Same table again
              </button>
            )}
            {onExit && <button onClick={onExit}>New game</button>}
            <button
              class="secondary"
              data-testid="main-menu"
              onClick={() => {
                // Hot-seat: drop the finished game so the main menu starts fresh.
                onExit?.();
                location.assign('/');
              }}
            >
              Main menu
            </button>
          </div>
        </div>
      )}

      {seat !== null && (
        <div class={`panel actions ${yourTurn ? 'your-turn' : ''}`} data-testid="actions">
          {showingResult && lastResult && (
            <HandResultCard result={lastResult} view={view} names={names} seat={seat} onNext={dismissResult} />
          )}
          {yourTurn && (
            <div class="your-turn-banner" data-testid="your-turn">
              Your turn
            </div>
          )}
          {notice ? (
            <div class="called-notice" data-testid="called-notice">
              {notice}
            </div>
          ) : (
            side && (
              <div class="side-line" data-testid="side-line">
                {side}
              </div>
            )
          )}
          <ActionPanel
            client={client}
            selected={selected}
            keepCount={keepCount}
            givable={givable}
            bidSuit={bidSuit}
            setBidSuit={setBidSuit}
            bidAmount={bidAmount}
            setBidAmount={setBidAmount}
            run={run}
          />
          {kittyRecap && (
            <div class="kitty-recap" data-testid="kitty-recap">
              <span class="small muted">The kitty had:</span>
              <div class="row wrap">
                {sortHand(h.kittyCards ?? [], trump).map((c) => (
                  <Card key={c} card={c} width={34} dim={!!trump && !isPlayable(c, trump)} />
                ))}
              </div>
              <span class="small">{kittyRecap}</span>
            </div>
          )}
          {kittyRow.length > 0 && (
            <div class="kitty-row" data-testid="kitty">
              <h3>From the kitty</h3>
              <div class="hand">
                {sortHand(kittyRow, trump).map((c) => (
                  <Card key={c} card={c} testid={`kitty-${c}`} {...cardProps(c)} />
                ))}
              </div>
              <h3>Your hand</h3>
            </div>
          )}
          <div class={`hand ${dealing ? 'dealing' : ''}`} data-testid="hand" key={`hand-${h.number}`}>
            {sortedHand.map((c, i) => (
              <Card key={c} card={c} testid={`hand-${c}`} style={{ '--i': i }} {...cardProps(c)} />
            ))}
          </div>
        </div>
      )}

      {seat === null && showingResult && lastResult && (
        <div class="panel">
          <HandResultCard result={lastResult} view={view} names={names} seat={seat} onNext={dismissResult} />
        </div>
      )}

      <ScoreSheet view={view} names={names} />

      <div class="panel log" data-testid="log">
        <h2>Log</h2>
        <ol>
          {client.log
            .slice(-40)
            .reverse()
            .map((l) => (
              <li key={l.id}>{l.text}</li>
            ))}
        </ol>
      </div>

      <div class="row footer-links">
        <button class="link" data-testid="open-rules" onClick={() => setShowRules(true)}>
          Rules
        </button>
        <button
          class="link"
          data-testid="mute"
          aria-pressed={muted}
          onClick={() => {
            setMuted(!muted);
            setMutedState(!muted);
          }}
        >
          {muted ? '🔇 Sound off' : '🔊 Sound on'}
        </button>
        {wakeLockSupported() && (
          <button class="link" data-testid="keep-awake" aria-pressed={keepAwake} onClick={() => setKeepAwake(!keepAwake)}>
            {keepAwake ? '☀ Screen stays on' : '🌙 Screen can sleep'}
          </button>
        )}
        {extraControls}
        {client.undo && (
          <button class="link" data-testid="undo" onClick={() => client.undo?.()}>
            Undo my last move
          </button>
        )}
        {onExit && phase.kind !== 'gameOver' && (
          <button class="link" onClick={() => confirm('Quit this game?') && onExit()}>
            Quit game
          </button>
        )}
      </div>
      {showRules && <RulesSheet config={view.config} onClose={() => setShowRules(false)} />}
    </div>
  );
}

/**
 * Where seat `i` sits at the dealer's-seat table: you at the bottom, everyone
 * else spread along the top arc in play order, the player to your left at the
 * far left. Returned as a direction from the table's middle (y grows down).
 */
function seatDirection(i: number, viewer: number | null, n: number): { x: number; y: number } {
  const k = (i - (viewer ?? 0) + n) % n;
  if (k === 0) return { x: 0, y: 1 };
  const theta = Math.PI * (1 - k / n);
  return { x: Math.cos(theta), y: -Math.sin(theta) };
}

/**
 * Seat centres (px) for the other players along the far arc of a `width`×`height`
 * table. They're spaced by arc length rather than angle: on a narrow, tall
 * phone table equal angles crowd the seats together across the top.
 */
function arcSeats(count: number, width: number, height: number): { left: number; top: number }[] {
  const phone = width < 600;
  const halfBox = phone ? (count > 5 ? 35 : 39) : 50;
  const rx = Math.max(40, width / 2 - halfBox - 2);
  const top = phone ? 30 : 40;
  const ry = Math.max(40, height * (phone ? 0.76 : 0.74) - top);
  const at = (theta: number) => ({ left: width / 2 + rx * Math.cos(theta), top: top + ry * (1 - Math.sin(theta)) });
  const steps = 240;
  const lengths = [0];
  for (let i = 1; i <= steps; i++) {
    const a = at(Math.PI * (1 - (i - 1) / steps));
    const b = at(Math.PI * (1 - i / steps));
    lengths.push(lengths[i - 1]! + Math.hypot(b.left - a.left, b.top - a.top));
  }
  const total = lengths[steps]!;
  return Array.from({ length: count }, (_, k) => {
    const want = (total * (k + 1)) / (count + 1);
    const i = lengths.findIndex((l) => l >= want);
    return at(Math.PI * (1 - Math.max(0, i) / steps));
  });
}

/** Tracks an element's size, measured before paint so seats don't jump. */
function useSize<T extends HTMLElement>(): [{ current: T | null }, { width: number; height: number }] {
  const ref = useRef<T>(null);
  const [size, setSize] = useState({ width: 760, height: 340 });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => setSize((old) => (old.width === el.clientWidth && old.height === el.clientHeight ? old : { width: el.clientWidth, height: el.clientHeight }));
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, size];
}

function seatInfo(view: GameView, i: number, names: readonly string[], viewer: number | null): string {
  const h = view.hand;
  const bid = h.bids[i];
  const tag = roleTag(view, i, names, viewer);
  return [
    view.phase.kind === 'bid' && bid != null ? (bid === 0 ? 'pass' : `bid ${bidLabel(bid, h.bidSuits[i] ?? null)}`) : null,
    tag === 'Bidder' ? `bid ${bidLabel(h.bid!, h.trump)}` : tag,
    view.phase.kind === 'play' ? `${h.points[i]} pts` : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

function roleOf(view: GameView, i: number): string {
  const h = view.hand;
  return i === h.bidder ? 'bidder' : h.partner !== 'unknown' && h.partner === i ? 'partner' : '';
}

/**
 * A dealer's-seat table: the felt is a half oval whose near edge curves in
 * toward you, with the other players around the far arc and your own seat as a
 * short rectangle at the near edge. `children` go on the felt.
 */
function TableRing({
  client,
  waitingOn,
  dealing,
  children,
}: {
  client: GameClient;
  waitingOn: number[];
  dealing: boolean;
  children: ComponentChildren;
}) {
  const { view, names, seat, bots } = client;
  const h = view.hand;
  const n = names.length;
  const [ref, size] = useSize<HTMLDivElement>();
  // Others in play order, the player to your left first (at the far left).
  const others = Array.from({ length: n }, (_, k) => ((seat ?? 0) + 1 + k) % n).filter((i) => i !== seat).slice(0, seat === null ? n : n - 1);
  const spots = arcSeats(others.length, size.width, size.height);
  const front = { left: size.width / 2, top: size.height * 0.9 };
  const spotOf = (i: number) => (i === seat ? front : spots[others.indexOf(i)] ?? front);
  const middle = { left: size.width / 2, top: size.height * 0.5 };
  return (
    <div class={`ring seats-${n}`} data-testid="seats" ref={ref}>
      <svg class="felt-shape" viewBox="0 0 100 60" preserveAspectRatio="none" aria-hidden="true">
        <path d="M 3 57 A 47 54 0 0 1 97 57 Q 50 42 3 57 Z" />
      </svg>
      <div class="felt">{children}</div>
      {others.map((i, k) => {
        const name = names[i]!;
        const style = { left: `${spots[k]!.left}px`, top: `${spots[k]!.top}px` };
        const count = h.handCounts[i] ?? 0;
        const active = waitingOn.includes(i);
        const sideMark = sideOf(view, seat, i);
        return (
          <div key={i} style={style} class={`seat ${active ? 'active' : ''} ${roleOf(view, i)} ${sideMark ?? ''}`} data-testid={`seat-${i}`}>
            <div class="seat-name">
              {i === h.dealer && <span class="badge" title="Dealer">D</span>}
              {name}
              {bots[i] && <span class="muted"> 🤖</span>}
            </div>
            <div class="seat-score" data-testid={`score-${i}`}>
              {view.scores[i]}
            </div>
            <div class="seat-info">{seatInfo(view, i, names, seat)}</div>
            <div class="seat-cards muted small" aria-label={`${count} cards`}>
              {sideMark && (
                <span class={`side-mark ${sideMark}`} data-testid={`side-${i}`}>
                  {sideMark === 'with' ? 'with' : 'vs'}
                </span>
              )}
              {count > 0 ? (
                <>
                  <CardBack width={10} /> {count}
                </>
              ) : (
                'out'
              )}
              {active && (
                <span class="thinking" aria-label="their turn">
                  <i />
                  <i />
                  <i />
                </span>
              )}
            </div>
          </div>
        );
      })}
      {seat !== null && (
        <div
          style={{ left: `${front.left}px`, top: `${front.top}px` }}
          class={`seat front ${waitingOn.includes(seat) ? 'active' : ''} ${roleOf(view, seat)}`}
          data-testid={`seat-${seat}`}
        >
          {seat === h.dealer && <span class="badge" title="Dealer">D</span>}
          <strong>
            {names[seat]}
            {names[seat] !== 'You' && ' (you)'}
          </strong>
          <span class="seat-score" data-testid={`score-${seat}`}>
            {view.scores[seat]}
          </span>
          <span class="seat-info">{seatInfo(view, seat, names, seat)}</span>
        </div>
      )}
      {dealing && (
        <div class="deal" aria-hidden="true" data-testid="dealing">
          <div class="deck" style={{ left: `${middle.left}px`, top: `${middle.top}px` }}>
            <CardBack width={26} />
            <CardBack width={26} />
          </div>
          {Array.from({ length: n }, (_, k) => {
            // One card to each seat in deal order, starting left of the dealer.
            const to = spotOf((h.dealer + 1 + k) % n);
            const style = {
              left: `${middle.left}px`,
              top: `${middle.top}px`,
              '--to-x': `${to.left - middle.left}px`,
              '--to-y': `${to.top - middle.top}px`,
              '--k': k,
            };
            return (
              <div class="fly" style={style} key={k}>
                <CardBack width={20} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function resultHeadline(result: GameView['history'][number], names: readonly string[], seat: number | null): string {
  const who = (i: number) => (i === seat ? 'You' : names[i]!);
  const side = result.partner === null ? who(result.bidder) : `${who(result.bidder)} and ${who(result.partner)}`;
  const contract = `${bidLabel(result.bid, null)} ${SUIT_GLYPH[result.trump]}`;
  return result.made ? `${side} made it! ${contract}, took ${result.bidderSidePoints}` : `${side} went set: ${result.bidderSidePoints} of ${contract}`;
}

/** What just happened in the hand: the headline and each player's change. */
function HandResultCard({
  result,
  view,
  names,
  seat,
  onNext,
}: {
  result: GameView['history'][number];
  view: GameView;
  names: readonly string[];
  seat: number | null;
  onNext: () => void;
}) {
  const who = (i: number) => (i === seat ? 'You' : names[i]!);
  const headline = resultHeadline(result, names, seat);
  const bidding = (i: number) => i === result.bidder || i === result.partner;
  return (
    <div class={`hand-result ${result.made ? 'made' : 'set'}`} data-testid="hand-result">
      <strong>{headline}</strong>
      <ul>
        {result.deltas.map((d, i) => (
          <li key={i} class={`${bidding(i) ? 'bidside' : ''} ${i === seat ? 'me' : ''}`}>
            <span>{who(i)}</span>
            <span class={d < 0 ? 'neg' : 'pos'}>{d > 0 ? `+${d}` : d}</span>
            <span class="muted">→ {view.scores[i]}</span>
          </li>
        ))}
      </ul>
      {view.phase.kind !== 'gameOver' && (
        <button class="small primary" data-testid="next-hand" onClick={onNext}>
          Next hand
        </button>
      )}
    </div>
  );
}

function Trick({ view, names, seat }: { view: GameView; names: readonly string[]; seat: number | null }) {
  const h = view.hand;
  const n = view.config.playerCount;
  const plays = h.trick && h.trick.plays.length > 0 ? h.trick.plays : h.lastTrick?.plays ?? [];
  const done = !(h.trick && h.trick.plays.length > 0) && h.lastTrick;
  if (plays.length === 0) {
    return (
      <div class="trick empty muted">
        {view.phase.kind === 'bid' || view.phase.kind === 'call' ? `Kitty: ${h.kittyCount} cards` : ''}
      </div>
    );
  }
  return (
    <div class={`trick ${done ? 'done' : ''}`} data-testid="trick">
      {plays.map((p) => {
        const won = done && h.lastTrick!.winner === p.player;
        const from = seatDirection(p.player, seat, n);
        // Each card slides in from its player's side of the table.
        const style = { '--from-x': `${from.x * 140}px`, '--from-y': `${from.y * 110}px` };
        return (
          <div class={`played ${won ? 'won' : ''}`} key={p.card} style={style}>
            <Card card={p.card} width={58} />
            <span class="small" title={won ? `${names[p.player]} took it` : undefined}>
              {won && '✔ '}
              {names[p.player]}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function ActionPanel({
  client,
  selected,
  keepCount,
  givable,
  bidSuit,
  setBidSuit,
  bidAmount,
  setBidAmount,
  run,
}: {
  client: GameClient;
  selected: CardCode[];
  keepCount: number;
  givable: CardCode[];
  bidSuit: Suit | null;
  setBidSuit: (s: Suit | null) => void;
  bidAmount: number | null;
  setBidAmount: (n: number | null) => void;
  run: (a: Action) => void;
}) {
  const { view, seat, legal } = client;
  const h = view.hand;
  const phase = view.phase;
  const mine = seat !== null ? (h.hands[seat] ?? []) : [];

  const bids = [...new Set(legal.flatMap((a) => (a.type === 'bid' ? [a.amount] : [])))];
  if (bids.length > 0) {
    const amounts = bids.filter((b) => b > 0);
    const ready = bidAmount !== null && bidSuit !== null;
    // Number and suit can be picked in either order; nothing is sent until "Bid".
    return (
      <div class="bid-panel" data-testid="bid-panel">
        <div class="row wrap">
          {amounts.map((b) => (
            <button
              key={b}
              class={bidAmount === b ? 'active' : ''}
              data-testid={`bid-${b}`}
              aria-pressed={bidAmount === b}
              onClick={() => setBidAmount(bidAmount === b ? null : b)}
            >
              {b === MOON_BID ? 'Shoot the moon' : b}
            </button>
          ))}
        </div>
        <div class="row wrap">
          {SUITS.map((s) => (
            <button
              key={s}
              class={`suit-button ${bidSuit === s ? 'active' : 'secondary'}`}
              data-testid={`bid-suit-${s}`}
              aria-pressed={bidSuit === s}
              onClick={() => setBidSuit(bidSuit === s ? null : s)}
            >
              <span class={`suit ${s}`}>{SUIT_GLYPH[s]}</span> {SUIT_NAMES[s]}
            </button>
          ))}
        </div>
        {!ready && <p class="muted small">Pick a number and a suit, in either order. You can tap a card for its suit.</p>}
        <div class="row wrap bid-final">
          {ready && (
            <button class="primary" data-testid="bid-submit" onClick={() => run({ type: 'bid', amount: bidAmount!, suit: bidSuit! })}>
              Bid {bidAmount === MOON_BID ? 'the moon' : bidAmount} {SUIT_GLYPH[bidSuit!]}
            </button>
          )}
          {bids.includes(0) && (
            <button class="pass" data-testid="bid-0" onClick={() => run({ type: 'bid', amount: 0 })}>
              Pass
            </button>
          )}
        </div>
      </div>
    );
  }

  // Your own cards aren't offered: calling one would just mean playing alone by accident.
  // (If you somehow hold every playable card, you get them all back so you can still call.)
  const callable = legal.flatMap((a) => (a.type === 'call' ? [a.card] : []));
  const notMine = callable.filter((c) => !mine.includes(c));
  const calls = notMine.length > 0 ? notMine : callable;
  if (calls.length > 0) {
    return (
      <div class="call-panel" data-testid="call-panel">
        <p class="muted small">Call your partner's card.</p>
        <div class="call-cards">
          {calls.map((c) => (
            <div class="call-option" key={c}>
              <Card card={c} width={44} testid={`call-${c}`} onClick={() => run({ type: 'call', card: c })} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (phase.kind === 'pass' && seat === h.bidder) {
    const partner = typeof h.partner === 'number' ? client.names[h.partner] : 'your partner';
    return (
      <div class="row wrap" data-testid="pass-panel">
        <span class="muted">Tap kitty cards to pass them to {partner}.</span>
        <button data-testid="give" onClick={() => run({ type: 'give', cards: selected.filter((c) => givable.includes(c)) })}>
          {selected.length === 0 ? 'Keep them all' : `Pass ${selected.length} card${selected.length === 1 ? '' : 's'}`}
        </button>
      </div>
    );
  }

  if (phase.kind === 'trim' && seat !== null && phase.pending[seat]) {
    return (
      <div class="row wrap" data-testid="keep-panel">
        <span class="muted">
          Keep {keepCount} playable card{keepCount === 1 ? '' : 's'}. The rest are thrown away.
        </span>
        <button data-testid="keep" disabled={selected.length !== keepCount} onClick={() => run({ type: 'keep', cards: selected })}>
          Keep {selected.length}/{keepCount}
        </button>
      </div>
    );
  }

  if (phase.kind === 'play' && phase.turn === seat) {
    const out = outWinner(view);
    return (
      <p class="muted" data-testid="play-hint">
        {out !== null ? `${client.names[out]} took that trick with their last card, so you lead. ` : ''}Tap a card to play it.
      </p>
    );
  }
  if (phase.kind === 'gameOver') return null;
  const others = waiting(view).filter((i) => i !== seat);
  const doing: Record<string, string> = { bid: 'bid', call: 'call a partner', pass: 'pass cards', trim: 'choose their cards', play: 'play' };
  return (
    <p class="muted" data-testid="waiting">
      Waiting for {others.map((i) => client.names[i]).join(', ') || 'the others'} to {doing[phase.kind]}…
    </p>
  );
}

/** A small B (won the bid) or P (partner) after a score. */
function RoleMark({ i, bidder, partner }: { i: number; bidder: number | null; partner: number | null | 'unknown' }) {
  if (i === bidder) return <sup class="role" title="Won the bid">B</sup>;
  if (partner !== 'unknown' && i === partner) return <sup class="role" title="Partner">P</sup>;
  return null;
}

function ScoreSheet({ view, names }: { view: GameView; names: readonly string[] }) {
  const h = view.hand;
  const live = view.phase.kind !== 'gameOver';
  const liveSide = (i: number) => i === h.bidder || (h.partner !== 'unknown' && i === h.partner);
  return (
    <div class="panel scores" data-testid="score-sheet">
      <h2>Scores (to {view.config.rules.targetScore})</h2>
      <div class="table-scroll">
        <table>
          <thead>
            <tr>
              <th>Hand</th>
              {names.map((n, i) => (
                <th key={i}>{n}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* Newest first under the running total, with the hand being played on top. */}
            <tr class="total">
              <td>Total</td>
              {view.scores.map((s, i) => (
                <td key={i}>{s}</td>
              ))}
            </tr>
            {live && (
              <tr class="live" data-testid="live-hand">
                <td>
                  ▶ {h.number} <span class="muted small">{h.bid !== null && h.trump ? bidLabel(h.bid, h.trump) : ''}</span>
                </td>
                {names.map((_, i) => {
                  const bid = h.bids[i];
                  const text =
                    view.phase.kind === 'bid'
                      ? bid == null ? '' : bid === 0 ? 'pass' : bidLabel(bid, h.bidSuits[i] ?? null)
                      : view.phase.kind === 'play'
                        ? String(h.points[i])
                        : '';
                  return (
                    <td key={i} class={liveSide(i) ? 'bidside' : ''}>
                      <span class="small">{text}</span>
                      <RoleMark i={i} bidder={h.bidder} partner={h.partner} />
                    </td>
                  );
                })}
              </tr>
            )}
            {view.history
              .slice()
              .reverse()
              .map((r) => (
                <tr
                  key={r.hand}
                  title={`${names[r.bidder]} bid ${bidLabel(r.bid, null)} in ${SUIT_NAMES[r.trump]}${r.partner !== null ? ` with ${names[r.partner]}` : ' alone'}: ${r.made ? 'made' : 'set'}`}
                >
                  <td>
                    {r.hand} <span class="muted small">{bidLabel(r.bid, r.trump)}{r.made ? '' : '✗'}</span>
                  </td>
                  {r.deltas.map((d, i) => (
                    <td key={i} class={i === r.bidder || i === r.partner ? 'bidside' : ''}>
                      {d > 0 ? `+${d}` : d}
                      <RoleMark i={i} bidder={r.bidder} partner={r.partner} />
                    </td>
                  ))}
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      <p class="muted small legend">B won the bid · P partner · ✗ set{live ? ' · ▶ this hand so far' : ''}</p>
    </div>
  );
}
